/**
 * Payment Proof Upload Page
 * Page for users to upload payment proof after their request is accepted
 */

import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Icon from "../../components/AppIcon";
import PublicFooter from "../../components/public/PublicFooter";
import PublicHeader from "../../components/public/PublicHeader";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select, { type SelectOption } from "../../components/ui/Select";
import Textarea from "../../components/ui/Textarea";
import { useToast } from "../../contexts/ToastContext";
import { useListPublicPaymentAccountsQuery } from "../../store/api/paymentAccountsApi";
import { useCreatePaymentProofMutation } from "../../store/api/paymentProofsApi";
import { useGetPublicPropertyRequestQuery } from "../../store/api/propertyRequestsApi";

const PaymentProofUploadPage: React.FC = () => {
	const navigate = useNavigate();
	const params = useParams();
	const requestId = (params as { requestId?: string }).requestId;
	const [searchParams] = useSearchParams();
	const { success, error: showError } = useToast();

	// Get request ID from params or query
	const propertyRequestId = requestId || searchParams.get("request_id") || "";

	const { data: requestData, isLoading: isLoadingRequest } =
		useGetPublicPropertyRequestQuery(propertyRequestId, {
			skip: !propertyRequestId,
		});
	const { data: accountsData, isLoading: isLoadingAccounts } =
		useListPublicPaymentAccountsQuery();
	const [createPaymentProof, { isLoading: isSubmitting }] =
		useCreatePaymentProofMutation();

	const [formData, setFormData] = useState({
		amount: "",
		payment_date: new Date().toISOString().split("T")[0],
		payment_method: "",
		transaction_reference: "",
		notes: "",
		proof_file: null as File | null,
	});

	const paymentMethodOptions: SelectOption[] = [
		{ value: "Bank Transfer", label: "Bank Transfer" },
		{ value: "Cash Deposit", label: "Cash Deposit" },
		{ value: "Mobile Transfer", label: "Mobile Transfer" },
		{ value: "Cheque", label: "Cheque" },
		{ value: "Other", label: "Other" },
	];

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			// Validate file type
			const validTypes = [
				"image/jpeg",
				"image/png",
				"image/jpg",
				"application/pdf",
			];
			if (!validTypes.includes(file.type)) {
				showError("Please upload a valid image (JPEG, PNG) or PDF file");
				return;
			}
			// Validate file size (max 5MB)
			if (file.size > 5 * 1024 * 1024) {
				showError("File size must be less than 5MB");
				return;
			}
			setFormData({ ...formData, proof_file: file });
		}
	};

	const convertFileToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!propertyRequestId) {
			showError("Property request ID is required");
			return;
		}

		if (!formData.proof_file) {
			showError("Please upload a payment proof document");
			return;
		}

		if (!formData.amount || parseFloat(formData.amount) <= 0) {
			showError("Please enter a valid payment amount");
			return;
		}

		if (!formData.payment_method) {
			showError("Please select a payment method");
			return;
		}

		try {
			// Convert file to base64
			const base64File = await convertFileToBase64(formData.proof_file);

			await createPaymentProof({
				property_request_id: propertyRequestId,
				amount: parseFloat(formData.amount),
				payment_date: new Date(formData.payment_date).toISOString(),
				payment_method: formData.payment_method,
				transaction_reference: formData.transaction_reference || undefined,
				notes: formData.notes || undefined,
				proof_document_file: base64File,
			}).unwrap();

			success(
				"Payment proof uploaded successfully! Admin will review your submission.",
			);
			navigate("/");
		} catch (err: any) {
			showError(
				err?.data?.detail ||
					"Failed to upload payment proof. Please try again.",
			);
		}
	};

	if (isLoadingRequest) {
		return (
			<div className="min-h-screen bg-background">
				<PublicHeader />
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
					<div className="flex items-center justify-center">
						<Icon
							name="Loader2"
							size={32}
							className="animate-spin text-primary"
						/>
					</div>
				</div>
				<PublicFooter />
			</div>
		);
	}

	if (!requestData) {
		return (
			<div className="min-h-screen bg-background">
				<PublicHeader />
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
					<div className="text-center">
						<h2 className="text-2xl font-semibold text-foreground mb-3">
							Request Not Found
						</h2>
						<p className="text-muted-foreground mb-6">
							The property request you're looking for doesn't exist.
						</p>
						<Button onClick={() => navigate("/")}>Back to Home</Button>
					</div>
				</div>
				<PublicFooter />
			</div>
		);
	}

	if (requestData.status !== "accepted") {
		return (
			<div className="min-h-screen bg-background">
				<PublicHeader />
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
					<div className="text-center">
						<h2 className="text-2xl font-semibold text-foreground mb-3">
							Request Not Accepted
						</h2>
						<p className="text-muted-foreground mb-6">
							Your property request must be accepted before you can upload
							payment proof.
						</p>
						<Button onClick={() => navigate("/")}>Back to Home</Button>
					</div>
				</div>
				<PublicFooter />
			</div>
		);
	}

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	return (
		<div className="min-h-screen bg-background">
			<PublicHeader />
			<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="max-w-3xl mx-auto">
					{/* Header */}
					<div className="mb-8">
						<Button
							variant="ghost"
							onClick={() => navigate("/")}
							className="mb-4"
						>
							<Icon name="ArrowLeft" size={16} className="mr-2" />
							Back to Home
						</Button>
						<h1 className="text-3xl font-bold text-foreground mb-2">
							Upload Payment Proof
						</h1>
						<p className="text-muted-foreground">
							Upload proof of payment for your {requestData.request_type}{" "}
							request
						</p>
					</div>

					{/* Payment Account Details */}
					{accountsData && accountsData.results.length > 0 && (
						<div className="bg-card border border-border rounded-lg p-6 mb-6">
							<h2 className="text-xl font-semibold text-foreground mb-4">
								Payment Account Details
							</h2>
							<div className="space-y-4">
								{accountsData.results.map((account) => (
									<div
										key={account.id}
										className="bg-muted/30 rounded-lg p-4 border border-border"
									>
										<p className="font-semibold text-foreground mb-2">
											{account.account_name}
										</p>
										<div className="space-y-1 text-sm">
											<p>
												<span className="text-muted-foreground">
													Account Number:
												</span>{" "}
												<span className="font-mono font-medium text-foreground">
													{account.account_number}
												</span>
											</p>
											<p>
												<span className="text-muted-foreground">Bank:</span>{" "}
												<span className="text-foreground">
													{account.bank_name}
												</span>
											</p>
											{account.notes && (
												<p className="text-muted-foreground mt-2">
													{account.notes}
												</p>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Request Details */}
					<div className="bg-card border border-border rounded-lg p-6 mb-6">
						<h2 className="text-xl font-semibold text-foreground mb-4">
							Request Details
						</h2>
						<div className="space-y-2 text-sm">
							<p>
								<span className="text-muted-foreground">Property:</span>{" "}
								<span className="text-foreground font-medium">
									{requestData.property_name}
								</span>
							</p>
							<p>
								<span className="text-muted-foreground">Request Type:</span>{" "}
								<span className="text-foreground font-medium capitalize">
									{requestData.request_type}
								</span>
							</p>
							<p>
								<span className="text-muted-foreground">Amount:</span>{" "}
								<span className="text-foreground font-medium">
									{formatCurrency(
										requestData.offer_amount || requestData.quoted_amount,
									)}
									{requestData.request_type === "lease" ? "/month" : ""}
								</span>
							</p>
						</div>
					</div>

					{/* Upload Form */}
					<div className="bg-card border border-border rounded-lg p-6">
						<h2 className="text-xl font-semibold text-foreground mb-6">
							Payment Information
						</h2>
						<form onSubmit={handleSubmit} className="space-y-4">
							<Input
								label="Payment Amount"
								type="number"
								step="0.01"
								placeholder="Enter payment amount"
								value={formData.amount}
								onChange={(e) =>
									setFormData({ ...formData, amount: e.target.value })
								}
								required
							/>

							<Input
								label="Payment Date"
								type="date"
								value={formData.payment_date}
								onChange={(e) =>
									setFormData({ ...formData, payment_date: e.target.value })
								}
								required
							/>

							<Select
								label="Payment Method"
								value={formData.payment_method}
								onValueChange={(value) => {
									const selectedValue = Array.isArray(value) ? value[0] : value;
									setFormData({
										...formData,
										payment_method: String(selectedValue),
									});
								}}
								options={paymentMethodOptions}
								required
							/>

							<Input
								label="Transaction Reference (Optional)"
								placeholder="Enter transaction reference number"
								value={formData.transaction_reference}
								onChange={(e) =>
									setFormData({
										...formData,
										transaction_reference: e.target.value,
									})
								}
							/>

							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Payment Proof Document{" "}
									<span className="text-destructive">*</span>
								</label>
								<input
									type="file"
									accept="image/jpeg,image/png,image/jpg,application/pdf"
									onChange={handleFileChange}
									className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
									required
								/>
								<p className="text-xs text-muted-foreground mt-1">
									Upload a screenshot or PDF of your payment receipt (Max 5MB)
								</p>
								{formData.proof_file && (
									<p className="text-sm text-foreground mt-2">
										Selected: {formData.proof_file.name}
									</p>
								)}
							</div>

							<Textarea
								label="Additional Notes (Optional)"
								placeholder="Any additional information about your payment..."
								value={formData.notes}
								onChange={(e) =>
									setFormData({ ...formData, notes: e.target.value })
								}
								rows={4}
							/>

							<div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
								<Button
									variant="outline"
									onClick={() => navigate("/")}
									type="button"
								>
									Cancel
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting ? "Uploading..." : "Upload Proof"}
								</Button>
							</div>
						</form>
					</div>
				</div>
			</div>
			<PublicFooter />
		</div>
	);
};

export default PaymentProofUploadPage;
